import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { env, isConfigured } from './env';

// Single shared client. Uses the public anon key — RLS is the access gate.
// Null when env isn't configured yet (e.g. Phase 1 before secrets), so the
// app shell can still render the design system.
export const supabase: SupabaseClient | null = isConfigured.supabase
  ? createClient(env.supabaseUrl, env.supabaseAnonKey, {
      auth: { persistSession: true, autoRefreshToken: true },
    })
  : null;

/**
 * A client whose session lives in memory only — never localStorage, never the
 * URL. Anything that has to hold credentials mid-flow, before the user has
 * earned a real session, uses one of these and throws it away afterwards.
 *
 * The property that matters: a session on this client cannot be reloaded into,
 * cannot be read out of storage by another script, and disappears when the tab
 * does. So a half-finished flow leaves nothing behind that a later step could
 * skip to.
 */
export function probeClient(): SupabaseClient {
  return createClient(env.supabaseUrl, env.supabaseAnonKey, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
  });
}

// Verify a password WITHOUT persisting a session. The login flow uses this so a
// password-only session can never land in localStorage and bypass the emailed
// code on reload — the probe client holds its session in memory only and is
// discarded immediately. Returns an error message on a bad password.
//
// The sign-out is scope 'local': supabase-js defaults to 'global', which revoked
// every session the user had, so each sign-in on a laptop logged their phone
// out (bug report, 2026-10-09).
export async function verifyPassword(email: string, password: string): Promise<{ error?: string }> {
  if (!isConfigured.supabase) return { error: 'Auth is not configured.' };
  const probe = probeClient();
  const { error } = await probe.auth.signInWithPassword({ email, password });
  await probe.auth.signOut({ scope: 'local' }).catch(() => {});
  return { error: error?.message };
}
